import {
  GLOBAL_RESULT_FORMAT_KEY,
  ResultFormat,
} from "components/Button/ResultFormatButton/ResultFormatButton";
import { usePremiumFeatureModal } from "features/Subscription/components";
import path from "constants/path";
import { getSearchModeLabelKey } from "constants/searchModeConfig";
import useCredits from "features/User/hooks/useCredits";
import { SearchMode } from "helpers/api";
import pluralize from "helpers/pluralize";
import { MixpanelActionSource } from "helpers/services/mixpanel/events";
import useLabels from "hooks/useLabels";
import useSearchMode from "hooks/useSearchMode";
import React, { useCallback, useMemo, useRef, useState } from "react";
import { CreditedFeature } from "store/slices/subscription";
import { useSearchModeSelectorEvents } from "./hooks/useSearchModeSelectorEvents";
import {
  Button,
  DeepSearchIcon,
  ProSearchIcon,
  QuickSearchIcon,
} from "design_system";
import { useAppDispatch, useAppSelector } from "hooks/useStore";
import { clearSources, selectSources } from "store/slices/sources";
import { toast } from "react-toastify";
import Dropdown, {
  ComposedDropdownItemProps,
} from "components/CoDropdown/ComposedDropdown";
import { ChevronDown } from "lucide-react";
import { Placement } from "@floating-ui/react";
import useSubscription from "features/User/hooks/useSubscription";

interface SearchModeSelectorProps {
  isLoading?: boolean;
  hideLabelForMobile?: boolean;
  dropdownPlacement?: Placement;
  className?: string;
}

type SearchModeDropdownItemProps = ComposedDropdownItemProps & {
  key: SearchMode;
};

type SearchModeConfig = {
  key: SearchMode;
  icon: string;
  tag?: React.ReactNode;
};

const SEARCH_MODES: SearchModeConfig[] = [
  {
    key: SearchMode.SUMMARY,
    icon: "/icons/icon_summary.svg",
  },
  {
    key: SearchMode.PRO_ANALYSIS,
    icon: "/icons/icon_pro_analysis.svg",
  },
  {
    key: SearchMode.PRO_RESEARCH,
    icon: "/icons/icon_deep_search.svg",
  },
];

const SearchModeSelector: React.FC<SearchModeSelectorProps> = ({
  isLoading = false,
  hideLabelForMobile = false,
  dropdownPlacement = "bottom-start",
  className = "",
}) => {
  const triggerRef = useRef<HTMLButtonElement>(null);

  const {
    searchModeCreditsRemaining,
    isNotSignedIn,
    isPremium,
    isUsageSet,
    isOutOfDeepSearchCredits,
    getTotalCreditsForMode,
  } = useCredits();
  const { subscriptionTier } = useSubscription();
  const { openPremiumFeatureModal } = usePremiumFeatureModal();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [labels] = useLabels("features.threads.search-mode-selector");
  const { currentSearchMode, changeSearchMode } = useSearchMode();
  const { trackSearchModeSelection } = useSearchModeSelectorEvents();
  const dispatch = useAppDispatch();
  const sources = useAppSelector(selectSources);

  const getSearchModeIcon = (mode: SearchMode) => {
    switch (mode) {
      case SearchMode.SUMMARY:
        return <QuickSearchIcon />;
      case SearchMode.PRO_ANALYSIS:
        return <ProSearchIcon />;
      case SearchMode.PRO_RESEARCH:
        return <DeepSearchIcon />;
      default:
        return <ProSearchIcon />;
    }
  };

  const handleClickUpgradePremium = useCallback(
    async (mode: SearchMode) => {
      const feature =
        mode === SearchMode.PRO_RESEARCH
          ? CreditedFeature.DEEP_SEARCH
          : CreditedFeature.PRO_SEARCH;

      openPremiumFeatureModal({
        feature,
        disableProToggle: true,
        actionSource: MixpanelActionSource.SearchModeSelector,
      });
    },
    [openPremiumFeatureModal]
  );

  const handleModeSelect = (mode: SearchMode) => {
    const creditsRemaining = searchModeCreditsRemaining(mode);

    if (creditsRemaining !== null && creditsRemaining <= 0) {
      handleClickUpgradePremium(mode);
    } else {
      if (mode !== SearchMode.PRO_ANALYSIS && sources.length > 0) {
        toast.error(labels["pro-analysis-required-for-sources"]);
        dispatch(clearSources());
      }

      changeSearchMode(mode);
      trackSearchModeSelection(mode);

      // Set global result format to COMPACT when PRO_RESEARCH is selected
      if (mode === SearchMode.PRO_RESEARCH) {
        localStorage.setItem(
          GLOBAL_RESULT_FORMAT_KEY,
          JSON.stringify(ResultFormat.COMPACT)
        );
      }
    }
    setIsDropdownOpen(false);
  };

  // Filter out SUMMARY mode for logged out users since they have 0 credits for it
  const availableModes = isNotSignedIn
    ? SEARCH_MODES.filter((mode) => mode.key !== SearchMode.SUMMARY)
    : SEARCH_MODES;

  const dropdownItems: SearchModeDropdownItemProps[] = availableModes.map(
    (mode) => {
      const creditsRemaining = searchModeCreditsRemaining(mode.key);
      const maxCredits = getTotalCreditsForMode(mode.key);

      const usageText =
        maxCredits === 0
          ? labels["sign-up-to-use"]
          : creditsRemaining === 0 && isNotSignedIn
            ? labels["sign-up-for-more"]
            : pluralize(labels["credits-left"], {
                CREDITS_LEFT: creditsRemaining,
              });

      return {
        key: mode.key,
        title: labels[getSearchModeLabelKey(mode.key)].title,
        disclaimer: creditsRemaining !== null && usageText,
        description: labels[getSearchModeLabelKey(mode.key)].description,
        onClick: () => handleModeSelect(mode.key),
        isDisabled:
          isUsageSet && creditsRemaining !== null && creditsRemaining <= 0,
        dataTestId: `search-mode-${getSearchModeLabelKey(mode.key)}`,
        icon: getSearchModeIcon(mode.key),
      };
    }
  );

  const buttonLabel = useMemo(() => {
    return (
      labels[getSearchModeLabelKey(currentSearchMode)]?.title ||
      labels["unknown"]
    );
  }, [currentSearchMode, labels]);

  const footer = useMemo(() => {
    if (!isUsageSet) return;

    if (isNotSignedIn) {
      return (
        <Button
          variant="primary"
          size="sm"
          onClick={() => {
            window.location.href = path.SIGN_IN;
          }}
          className="w-full"
        >
          {labels["sign-up-for-more"]}
        </Button>
      );
    }
    if (!isPremium) {
      return (
        <Button
          variant="primary"
          size="sm"
          onClick={() => handleClickUpgradePremium(currentSearchMode)}
          className="w-full"
        >
          {labels["upgrade-for-unlimited"]}
        </Button>
      );
    }

    if (subscriptionTier === "premium" && isOutOfDeepSearchCredits) {
      return (
        <Button
          variant="primary"
          size="sm"
          onClick={() => handleClickUpgradePremium(currentSearchMode)}
          className="w-full"
        >
          {labels["upgrade"]}
        </Button>
      );
    }

    return undefined;
  }, [
    isNotSignedIn,
    isPremium,
    subscriptionTier,
    isOutOfDeepSearchCredits,
    isUsageSet,
    labels,
    currentSearchMode,
    handleClickUpgradePremium,
  ]);

  return (
    <div className="relative" data-intercom-target="Search mode selector">
      <Dropdown
        triggerRef={triggerRef}
        open={isDropdownOpen}
        onOpenChange={setIsDropdownOpen}
        placement={dropdownPlacement}
        mobileTitle={labels["mobile-title"]}
        renderInPortal={false}
      >
        <Dropdown.Section showBorder={false}>
          {dropdownItems.map((item) => (
            <Dropdown.RadioItem
              key={item.key}
              title={item.title}
              disclaimer={item.disclaimer}
              description={item.description}
              icon={item.icon}
              isActive={currentSearchMode === item.key}
              onClick={item.onClick}
              isDisabled={item.isDisabled}
            />
          ))}
        </Dropdown.Section>
        {footer && <Dropdown.Footer>{footer}</Dropdown.Footer>}
      </Dropdown>

      <Button
        ref={triggerRef}
        variant="flat"
        size="sm"
        iconLeft={getSearchModeIcon(currentSearchMode)}
        iconRight={<ChevronDown />}
        onClick={() => {
          setIsDropdownOpen(!isDropdownOpen);
        }}
        disabled={isLoading}
        data-testid="search-mode-selector-button"
        className={className}
        hideLabelForMobile={hideLabelForMobile}
      >
        {buttonLabel}
      </Button>
    </div>
  );
};

export default SearchModeSelector;
